import { Injectable } from '@angular/core';
import { MyInfoReq } from '@common/dto/account';
import { SearchReq, VmMatch } from '@common/dto/my';
import {
  ChatMessage,
  ChatMessageSummary,
  MatchesSummaryRes,
  MessagesSummaryItem,
  SmileSummary,
  UserNotification,
} from '@common/dto/my/chat';
import { SanitizedUser, UserID } from '@common/dto/user';
import { createStore, setProps, withProps } from '@ngneat/elf';
import {
  addEntities,
  entitiesPropsFactory,
  getActiveId,
  getEntity,
  setActiveId,
  setEntities,
  updateEntities,
  updateEntitiesByPredicate,
  upsertEntities,
  upsertEntitiesById,
  withActiveId,
} from '@ngneat/elf-entities';
import { Picture } from '../state/picture/picture.model';

export const { matchEntitiesRef, withMatchEntities } =
  entitiesPropsFactory('match');
export const { chatEntitiesRef, withChatEntities } =
  entitiesPropsFactory('chat');
export const { messageSummaryEntitiesRef, withMessageSummaryEntities } =
  entitiesPropsFactory('messageSummary');
export const { notificationEntitiesRef, withNotificationEntities } =
  entitiesPropsFactory('notification');
export const { pictureEntitiesRef, withPictureEntities } =
  entitiesPropsFactory('picture');

export interface StateProps {
  hasNoMatches: boolean;
  searchCriteria: SearchReq | undefined;
  user: SanitizedUser | undefined;
  messagesSummaryLoading: boolean;
}

@Injectable({ providedIn: 'root' })
export class StateRepository {
  private store;

  constructor() {
    this.store = this.createStore();
  }

  getValue() {
    return this.store.value;
  }

  addMatch(match: VmMatch) {
    this.store.update(addEntities(match, { ref: matchEntitiesRef }));
  }

  upsertMatch(match: VmMatch) {
    this.store.update(
      upsertEntitiesById(match.tblUserId, {
        updater: () => match,
        creator: () => match,
        ref: matchEntitiesRef,
      })
    );
  }

  setActiveMatch(id: VmMatch['tblUserId']) {
    this.store.update(setProps({ hasNoMatches: false }), setActiveId(id));
  }

  setHasNoMatches(hasNoMatches: boolean) {
    this.store.update(setProps({ hasNoMatches }));
  }

  setSearchCriteria(searchCriteria: SearchReq) {
    this.store.update(
      setProps((state) => {
        return state.user
          ? {
              searchCriteria,
              user: { ...state.user, isSearchCriteriaComplete: true },
            }
          : { searchCriteria };
      })
    );
  }

  updateSearchCriteria(searchCriteria: Partial<SearchReq>) {
    const currentSearchCriteria = this.store.getValue().searchCriteria;
    if (!currentSearchCriteria) {
      return;
    }

    this.store.update(
      setProps({
        searchCriteria: { ...currentSearchCriteria, ...searchCriteria },
      })
    );
  }

  update(state: Partial<StateProps>) {
    this.store.update(setProps(state));
  }

  updateUserProp<Key extends keyof SanitizedUser>(
    prop: Key,
    value: SanitizedUser[Key]
  ) {
    this.updateUser({ [prop]: value });
  }

  setAboutMe(aboutMe: string) {
    this.updateUser({ aboutMe });
  }

  setMyInfo({ dob, gender, userName }: MyInfoReq) {
    this.updateUser({ dob, gender, userName });
  }

  questionnaireComplete() {
    this.updateUser({ isQuestionnaireComplete: true, hasStartedLooking: true });
  }

  startLooking() {
    this.updateUser({ hasStartedLooking: true });
  }

  reactivateAccount() {
    this.updateUser({ active: true });
  }

  getActiveMatch() {
    const activeId = this.store.query(getActiveId);
    return this.store.query(getEntity(activeId, { ref: matchEntitiesRef }));
  }

  matchEntityExists(matchId: UserID) {
    return (
      this.store.query(getEntity(matchId, { ref: matchEntitiesRef })) != null
    );
  }

  clearActiveMatch() {
    this.store.update(
      setProps({ hasNoMatches: false }),
      setActiveId(undefined)
    );
  }

  setMatchesSummary(summary: MatchesSummaryRes) {
    this.store.update(
      setEntities(summary.userNotifications, { ref: notificationEntitiesRef }),
      setEntities(summary.messagesSummary, { ref: messageSummaryEntitiesRef })
    );
  }

  setMessagesSummaryLoading(loading: boolean) {
    this.store.update(setProps({ messagesSummaryLoading: loading }));
  }

  addChatMessage(chatMessage: ChatMessage) {
    this.store.update(addEntities([chatMessage], { ref: chatEntitiesRef }));
  }

  chatMessageReceived(summary: ChatMessageSummary) {
    const chatMessage = messageSummaryToChatMessage(summary, {
      isReceived: true,
    });

    // matchRecord may be undefined if this match was never loaded yet. For example,
    // if user loaded "Messages" page right away
    const matchRecordExists = this.matchEntityExists(summary.matchId);

    this.store.update(
      addEntities([chatMessage], { ref: chatEntitiesRef }),
      matchRecordExists
        ? updateEntities(summary.matchId, updateSmiledFrom(summary), {
            ref: matchEntitiesRef,
          })
        : doNotUpdateState,
      upsertEntitiesById(summary.matchId, {
        creator: messagesSummaryItemUpserter(summary, true),
        updater: messagesSummaryItemUpserter(summary, true),
        ref: messageSummaryEntitiesRef,
      }),
      upsertEntities(chatOrSmileSummaryToUserNotification(summary), {
        ref: notificationEntitiesRef,
      })
    );
  }

  chatMessageSent(summary: ChatMessageSummary) {
    const chatMessage = messageSummaryToChatMessage(summary, {
      isReceived: false,
    });

    this.store.update(
      addEntities([chatMessage], { ref: chatEntitiesRef }),
      updateEntities(summary.matchId, updateSmiledTo(summary), {
        ref: matchEntitiesRef,
      }),
      upsertEntitiesById(summary.matchId, {
        creator: messagesSummaryItemUpserter(summary, false),
        updater: messagesSummaryItemUpserter(summary, false),
        ref: messageSummaryEntitiesRef,
      })
    );
  }

  smileReceived(summary: SmileSummary) {
    // matchRecord may be undefined if this match was never loaded yet. For example,
    // if user loaded "Messages" page right away
    const matchRecordExists = this.matchEntityExists(summary.matchId);

    this.store.update(
      matchRecordExists
        ? updateEntities(summary.matchId, updateSmiledFrom(summary), {
            ref: matchEntitiesRef,
          })
        : doNotUpdateState,
      upsertEntities(chatOrSmileSummaryToUserNotification(summary), {
        ref: notificationEntitiesRef,
      })
    );
  }

  smileSent(summary: SmileSummary) {
    this.store.update(
      updateEntities(summary.matchId, updateSmiledTo(summary), {
        ref: matchEntitiesRef,
      })
    );
  }

  markLastMessageAsSeen(matchId: ChatMessage['matchId']) {
    this.store.update(
      updateEntities(
        matchId,
        { newMessagesCount: 0 },
        { ref: messageSummaryEntitiesRef }
      ),
      updateEntitiesByPredicate(
        (x) => x.matchId === matchId,
        { isNew: false },
        { ref: notificationEntitiesRef }
      ),
      updateEntitiesByPredicate(
        newMessagesReceivedFromMatch(matchId),
        { isNew: false },
        { ref: chatEntitiesRef }
      )
    );
  }

  sentMessagesToMatchAreSeen(matchId: ChatMessage['matchId']) {
    this.store.update(
      updateEntitiesByPredicate(
        newMessagesSentToMatch(matchId),
        { isNew: false },
        { ref: chatEntitiesRef }
      )
    );
  }

  upsertChatMessages(chatMessages: ChatMessage[]) {
    this.store.update(upsertEntities(chatMessages, { ref: chatEntitiesRef }));
  }

  markAllNotificationsAsRead() {
    this.store.update(
      updateEntitiesByPredicate(
        (x) => x.isNew,
        { isNew: false },
        { ref: notificationEntitiesRef }
      )
    );
  }

  updateUser(user: Partial<SanitizedUser>) {
    this.store.update(
      setProps((state) => {
        if (state.user) {
          return { ...state, user: { ...state.user, ...user } };
        }
        return state;
      })
    );
  }

  private createStore(): typeof store {
    const store = createStore(
      { name: 'my' },
      withProps<StateProps>({
        hasNoMatches: false,
        searchCriteria: undefined,
        user: undefined,
        messagesSummaryLoading: false,
      }),
      withMatchEntities<VmMatch, 'tblUserId'>({ idKey: 'tblUserId' }),
      withActiveId(),
      withChatEntities<ChatMessage, 'tblUserMessageId'>({
        idKey: 'tblUserMessageId',
      }),
      withMessageSummaryEntities<MessagesSummaryItem, 'matchId'>({
        idKey: 'matchId',
      }),
      withNotificationEntities<UserNotification, 'tblUserNotificationId'>({
        idKey: 'tblUserNotificationId',
      }),
      withPictureEntities<Picture>()
    );

    return store;
  }
}

function messageSummaryToChatMessage(
  summary: ChatMessageSummary,
  overrides: Partial<ChatMessage> & { isReceived: boolean }
): ChatMessage {
  return {
    tblUserMessageId: summary.tblUserMessageId,
    matchId: summary.matchId,
    text: summary.text,
    isNew: summary.isNew,
    insertDate: summary.insertDate,
    ...overrides,
  };
}

function updateSmiledFrom(summary: ChatMessageSummary | SmileSummary) {
  return (e: VmMatch) => {
    if (!e.smiledFrom) {
      return {
        ...e,
        smiledFrom: true,
        smiledFromDate: summary.insertDate,
      };
    }
    return e;
  };
}

function updateSmiledTo(summary: ChatMessageSummary | SmileSummary) {
  return (e: VmMatch) => {
    if (!e.smiledTo) {
      return {
        ...e,
        smiledTo: true,
        smiledToDate: summary.insertDate,
      };
    }
    return e;
  };
}

function messagesSummaryItemUpserter(
  summary: ChatMessageSummary,
  receivedLastMessage: boolean
) {
  return (
    matchIdOrItem: MessagesSummaryItem['matchId'] | MessagesSummaryItem
  ): MessagesSummaryItem => {
    const item = typeof matchIdOrItem !== 'string' ? matchIdOrItem : null;
    const newMessagesCount = receivedLastMessage
      ? (item?.newMessagesCount ?? 0) + 1
      : 0;

    const message: MessagesSummaryItem = {
      matchId: summary.matchId,
      matchUserName: summary.matchUserName,
      matchUserGender: summary.matchUserGender,
      matchUserActive: summary.matchUserActive,
      matchUserPictureId: summary.matchUserPictureId,
      matchUserPictureExtension: summary.matchUserPictureExtension,
      lastMessageInsertDate: summary.insertDate,
      lastMessageText: summary.text,
      receivedLastMessage,
      newMessagesCount,
    };

    return message;
  };
}

function chatOrSmileSummaryToUserNotification(
  summary: ChatMessageSummary | SmileSummary
) {
  const entry: UserNotification = {
    matchId: summary.matchId,
    tblNotificationId: summary.tblNotificationId,
    tblUserNotificationId: summary.tblUserNotificationId,
    isAReply: summary.isAReply,
    matchUserName: summary.matchUserName,
    matchUserGender: summary.matchUserGender,
    matchUserActive: summary.matchUserActive,
    matchUserPictureId: summary.matchUserPictureId,
    matchUserPictureExtension: summary.matchUserPictureExtension,
    insertDate: summary.insertDate,
    isNew: true,
  };
  return entry;
}

function newMessagesReceivedFromMatch(matchId: ChatMessage['matchId']) {
  return (entity: ChatMessage) => {
    return entity.matchId === matchId && entity.isNew && entity.isReceived;
  };
}

function newMessagesSentToMatch(matchId: ChatMessage['matchId']) {
  return (entity: ChatMessage) => {
    return entity.matchId === matchId && entity.isNew && !entity.isReceived;
  };
}

function doNotUpdateState<T>(state: T) {
  return state;
}
